import React from "react";
import { cn } from "@/lib/utils";

interface CarouselIndicatorProps {
  totalSlides: number;
  currentSlide: number;
  onDotClick: (index: number) => void;
  maxDots?: number;
  className?: string;
  getAriaLabel?: (slideNum: number) => string;
}

interface VisibleDotRange {
  start: number;
  end: number;
  centerStart: number;
  centerEnd: number;
}

const getVisibleDotIndices = (
  currentSlide: number,
  totalSlides: number,
  maxDots: number,
): VisibleDotRange => {
  if (totalSlides <= maxDots) {
    return {
      start: 0,
      end: totalSlides - 1,
      centerStart: 0,
      centerEnd: totalSlides - 1,
    };
  }

  const halfWindow = Math.floor(maxDots / 2);
  const bufferSize = 1;

  let centerStart: number;
  let centerEnd: number;

  if (currentSlide < halfWindow) {
    centerStart = 0;
    centerEnd = maxDots - 1;
  } else if (currentSlide >= totalSlides - halfWindow) {
    centerStart = totalSlides - maxDots;
    centerEnd = totalSlides - 1;
  } else {
    centerStart = currentSlide - halfWindow;
    centerEnd = currentSlide + halfWindow;
  }

  const start = Math.max(0, centerStart - bufferSize);
  const end = Math.min(totalSlides - 1, centerEnd + bufferSize);

  return { start, end, centerStart, centerEnd };
};

export const CarouselIndicator = ({
  totalSlides,
  currentSlide,
  onDotClick,
  maxDots = 7,
  className,
  getAriaLabel = (slideNum: number) => `Go to slide ${slideNum}`,
}: CarouselIndicatorProps): React.ReactElement => {
  const getDotSize = (index: number, currentSlide: number): string => {
    const distance = Math.abs(index - currentSlide);

    if (distance === 0) {
      return "h-[10px] w-[10px]";
    } else if (distance <= 1) {
      return "h-2 w-2";
    } else if (distance === 2) {
      return "h-[6px] w-[6px]";
    } else {
      return "h-1 w-1";
    }
  };

  const getDotOpacity = (index: number, currentSlide: number): string => {
    const distance = Math.abs(index - currentSlide);

    if (distance === 0) return "opacity-100";
    if (distance === 1) return "opacity-80";
    if (distance === 2) return "opacity-60";
    return "opacity-40";
  };

  if (totalSlides <= 1) {
    return <></>;
  }

  const { start, end, centerStart, centerEnd } = getVisibleDotIndices(
    currentSlide,
    totalSlides,
    maxDots,
  );

  const visibleDotCount = end - start + 1;

  return (
    <div className={cn("mt-4 flex justify-center gap-2", className)}>
      {Array.from({ length: visibleDotCount }).map((_, i) => {
        const actualIndex = start + i;
        const isInCenterWindow =
          actualIndex >= centerStart && actualIndex <= centerEnd;

        return (
          <button
            key={`dot-${actualIndex}`}
            className={cn(
              "rounded-full transition-all duration-300 ease-out",
              getDotSize(actualIndex, currentSlide),
              isInCenterWindow
                ? getDotOpacity(actualIndex, currentSlide)
                : "opacity-0",
              actualIndex === currentSlide
                ? "bg-foreground"
                : "bg-muted-foreground/30",
            )}
            onClick={() => onDotClick(actualIndex)}
            aria-label={getAriaLabel(actualIndex + 1)}
          />
        );
      })}
    </div>
  );
};
